import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { DynamicSupplyHardwareFiltersResponse, SimpleClusterForm } from "@/types";
import { getClusterHourCount } from "@/store/simpleCluster";

type ProcessorSelectionParams = {
  processorId: string;
  processorName: string;
  processorQuantityAvailable?: number;
};

type ProcessorChosenParams = {
  processorId: string;
  processorName: string;
  processorType?: string;
};

export type VerticalFlowAnalytics = ReturnType<typeof createVerticalFlowAnalytics>;

export const createVerticalFlowAnalytics = (clusterTypeLabel: string) => {
  const clusterType = clusterTypeLabel;
  const getPaymentContext = (params: {
    currency: string;
    amount?: number;
    userEmail?: string;
    paymentConfirmedInSeconds?: number;
  }) => {
    const { currency, amount, userEmail, paymentConfirmedInSeconds } = params;
    return {
      clusterType,
      amount,
      currency,
      payment_type: currency,
      userEmail: userEmail || undefined,
      paymentConfirmedInSeconds
    };
  };

  const trackCompletedStep = (stepTitle: string) => {
    trackEvent(AnalyticsEventType.COMPLETED_CLUSTER_CREATION_STEP, {
      clusterType,
      step: stepTitle
    });
  };

  const trackProcessorOptionSelected = (params: ProcessorSelectionParams) => {
    const { processorId, processorName, processorQuantityAvailable } = params;
    trackEvent(AnalyticsEventType.CLUSTER_PROCESSOR_OPTION_SELECTED, {
      clusterType,
      processorId,
      processorName,
      processorQuantityAvailable
    });
  };

  const trackProcessorSelected = (params: ProcessorChosenParams) => {
    const { processorId, processorName, processorType } = params;
    trackEvent(AnalyticsEventType.CLUSTER_PROCESSOR_SELECTED, {
      clusterType,
      step: "processor",
      processorId,
      processorName,
      processorType
    });
  };

  const trackImageSelected = (imageType: "general" | "data-science") => {
    trackEvent(AnalyticsEventType.CLUSTER_IMAGE_SELECTED, {
      clusterType,
      step: "imageSshKey",
      image: imageType === "general" ? "general purpose" : "data science"
    });
  };

  const trackSshKeyAdded = (type: "github" | "manual") => {
    trackEvent(AnalyticsEventType.CLUSTER_SSH_KEY_ADDED, {
      clusterType,
      key: type === "github" ? "github" : "manually"
    });
  };

  const trackSummaryEvents = (values: SimpleClusterForm, options?: { processorName?: string }) => {
    const hourCount =
      getClusterHourCount(values.durationQuantity as number, values.durationType) || 0;

    trackEvent(AnalyticsEventType.CLUSTER_DEPLOY_SELECTED, {
      averagePerCard:
        typeof values.hardwareInfo?.averagePerCard === "number"
          ? `${values.hardwareInfo?.averagePerCard} USDC/hr`
          : undefined,
      totalCostPerCard:
        typeof values.hardwareInfo?.averagePerCard === "number"
          ? `${(values.hardwareInfo?.averagePerCard || 0) * hourCount} USDC`
          : undefined,
      cost:
        typeof values.hardwareInfo?.cost === "number"
          ? `${values.hardwareInfo?.cost} USDC`
          : undefined,
      totalCost:
        typeof values.hardwareInfo?.totalCost === "number"
          ? `${values.hardwareInfo?.totalCost} USDC`
          : undefined,
      locations: values.locations,
      processorName: options?.processorName,
      quantity: values.hardwareQuantity,
      billingDuration: hourCount,
      totalMachines: parseInt(values.replicaCount || "1", 10),
      image: values.vmImageType === "general" ? "general purpose" : "data science",
      duration: values.durationType,
      durationTime: values.durationQuantity,
      paymentMethod: values.currency,
      payment_type: values.currency,
      amount: values.hardwareInfo?.totalCost
    });

    trackEvent(AnalyticsEventType.CLUSTER_DURATION_SELECTED, {
      clusterType,
      step: "summary",
      hours: hourCount
    });

    trackEvent(AnalyticsEventType.CLUSTER_PROCESSOR_QUANTITY_SELECTED, {
      clusterType,
      step: "summary",
      hardwareType: values.hardwareType,
      hardwareQuantity: values.hardwareQuantity
    });

    trackCompletedStep("Summary");
  };

  const trackPaymentButtonClicked = (params: {
    currency: string;
    amount?: number;
    userEmail?: string;
  }) => {
    const { currency, amount, userEmail } = params;
    trackEvent(AnalyticsEventType.CLUSTER_PAYMENT_BUTTON_CLICKED, {
      ...getPaymentContext({
        currency,
        amount,
        userEmail
      })
    });
    // Match v1 dataLayer payload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer?.push({
      event: AnalyticsEventType.CLUSTER_PAYMENT_BUTTON_CLICKED,
      type: currency,
      clusterType
    });
  };

  const trackDeployedSuccessfully = (params: {
    userId?: string;
    values: SimpleClusterForm;
    currency: string;
    amount?: number;
    deploymentTimeInSeconds: number;
    status?: string;
    userEmail?: string;
    paymentConfirmedInSeconds?: number;
  }) => {
    const {
      userId,
      values,
      currency,
      amount,
      deploymentTimeInSeconds,
      status = "submitted",
      userEmail,
      paymentConfirmedInSeconds
    } = params;

    trackEvent(AnalyticsEventType.CLUSTER_DEPLOYED_SUCCESSFULLY, {
      clusterType,
      status,
      deploymentTimeInSeconds,
      userId,
      clusterData: values,
      type: currency,
      payment_type: currency,
      amount,
      userEmail: userEmail || undefined,
      paymentConfirmedInSeconds
    });

    // Match v1 dataLayer payload (without amount)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer?.push({
      event: AnalyticsEventType.CLUSTER_DEPLOYED_SUCCESSFULLY,
      clusterType,
      status,
      deploymentTimeInSeconds,
      userId,
      clusterData: values,
      type: currency
    });
  };

  const trackPaymentApiFailed = (params: {
    currency: string;
    amount?: number;
    userEmail?: string;
    paymentConfirmedInSeconds?: number;
    apiAction: string;
    errorMessage: string;
    isNetworkError: boolean;
  }) => {
    const {
      currency,
      amount,
      userEmail,
      paymentConfirmedInSeconds,
      apiAction,
      errorMessage,
      isNetworkError
    } = params;
    trackEvent(AnalyticsEventType.CLUSTER_PAY_DEPLOY_API_FAILED, {
      ...getPaymentContext({
        currency,
        amount,
        userEmail,
        paymentConfirmedInSeconds
      }),
      apiAction,
      errorMessage,
      isNetworkError
    });
  };

  const trackApplyFilters = (params: {
    filters: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  }) => {
    const { filters } = params;
    trackEvent(AnalyticsEventType.CLUSTER_FILTER_APPLIED, {
      clusterType,
      filters: filters || undefined
    });
  };

  const trackGPUSearched = (params: { searchValue: string }) => {
    const { searchValue } = params;
    trackEvent(AnalyticsEventType.GPU_SEARCHED, {
      clusterType,
      searchedModel: searchValue
    });
  };

  const trackGPUMoreDetailsClicked = (params: {
    name: string;
    price: number;
    location?: string;
    supplier: string;
  }) => {
    const { name, price, location, supplier } = params;
    trackEvent(AnalyticsEventType.GPU_MORE_DETAILS_CLICKED, {
      clusterType,
      gpuName: name,
      price: price,
      location: location || undefined,
      supplier: supplier
    });
  };

  const trackRequestDevicesClicked = (params: { name: string }) => {
    const { name } = params;
    trackEvent(AnalyticsEventType.REQUEST_CLUSTER_CLICKED, {
      button_location: clusterType,
      device_name: name
    });
  };

  const trackRequestDevicesSubmitted = (params: { name: string }) => {
    const { name } = params;
    trackEvent(AnalyticsEventType.REQUEST_CLUSTER_SUBMITTED, {
      button_location: clusterType,
      device_name: name
    });
  };

  const trackIntermediateStepCompleted = (step: string, values: SimpleClusterForm) => {
    if (step === "environment") {
      trackCompletedStep("Configure Environment and SSH Keys");
      trackImageSelected(values.vmImageType === "general" ? "general" : "data-science");
      trackSshKeyAdded(values.sshType === "github" ? "github" : "manual");
    }
    if (step === "deployment") {
      trackCompletedStep("Deployment");
    }
  };

  return {
    trackCompletedStep,
    trackProcessorOptionSelected,
    trackProcessorSelected,
    trackImageSelected,
    trackSshKeyAdded,
    trackSummaryEvents,
    trackPaymentButtonClicked,
    trackDeployedSuccessfully,
    trackPaymentApiFailed,
    trackApplyFilters,
    trackGPUSearched,
    trackGPUMoreDetailsClicked,
    trackRequestDevicesClicked,
    trackRequestDevicesSubmitted,
    trackIntermediateStepCompleted
  };
};
